import { useEffect, useRef } from "react";
import type { Step } from "../../../shared/types";
import { imageUrl } from "../../api";
import { IconLayers } from "../../icons";
import { DRAWER_ID } from "./compact";
import { kindLabel } from "./StepList";

const two = (n: number) => String(n).padStart(2, "0");

/**
 * The compact Studio's left edge: one thumbnail per screen, click to edit it. Reordering, paths, adding screens and
 * shared screens hidden from a path live in the full list, which the button at the top opens as a drawer.
 */
export default function StepStrip({ seq, stepId, onSelect, listOpen, onToggleList }: {
  seq: Step[];
  stepId: string | undefined;
  onSelect: (id: string) => void;
  listOpen: boolean;
  onToggleList: () => void;
}) {
  const list = useRef<HTMLOListElement>(null);
  // Keep the screen being edited in view (it can change from the full list or after adding one). The list scrolls
  // itself rather than scrollIntoView, which would also scroll the Studio's overflow-hidden ancestors.
  useEffect(() => {
    const l = list.current, on = l?.querySelector(".thumb.on");
    if (!l || !on) return;
    const lr = l.getBoundingClientRect(), r = on.getBoundingClientRect();
    if (r.top < lr.top) l.scrollTop -= lr.top - r.top + 4;
    else if (r.bottom > lr.bottom) l.scrollTop += r.bottom - lr.bottom + 4;
  }, [stepId]);

  return (
    <nav className="studio-thumbs" aria-label="Screens">
      <button type="button" className={`thumbs-toggle ${listOpen ? "on" : ""}`} aria-expanded={listOpen} aria-controls={DRAWER_ID.steps}
        aria-label="All screens" title="All screens: paths, reorder, add a screen" onClick={onToggleList}>
        <IconLayers /><span className="count">{seq.length}</span>
      </button>
      <ol className="thumbs-list" ref={list}>
        {seq.map((s, i) => {
          const on = s.id === stepId;
          return (
            <li key={s.id}>
              <button type="button" className={`thumb ${on ? "on" : ""}`} onClick={() => onSelect(s.id)} aria-current={on ? "true" : undefined}
                aria-label={`Screen ${i + 1}: ${s.caption || kindLabel(s)}`} title={`${two(i + 1)} · ${s.caption || kindLabel(s)}`}>
                <span className="thumb-num">{two(i + 1)}</span>
                <span className={`thumb-img ${s.textCard ? "text" : ""}`}
                  style={s.textCard || s.mediaType === "video" ? undefined : { backgroundImage: `url(${imageUrl(s.imageKey)})` }}>
                  {s.textCard ? kindLabel(s) : s.mediaType === "video" ? <video src={imageUrl(s.imageKey)} muted preload="metadata" /> : null}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
